import * as Y from 'yjs';
import type { Shape } from '@frame/shared';

type YShapeMap = Y.Map<Y.Map<unknown>>;

// Origin tags for transactions that touch the shapes map. Undo manager
// listens to `LOCAL_ORIGINS` so peer-driven and seed writes never appear
// in the local undo stack. Keep this list in sync with useUndo.
export const LOCAL_ORIGIN_DRAG = 'local-drag';
export const LOCAL_ORIGIN_CREATE = 'local-create';
export const LOCAL_ORIGIN_EDIT = 'local-edit';
export const LOCAL_ORIGIN_DELETE = 'local-delete';
export const LOCAL_ORIGIN_RESIZE = 'local-resize';
export const LOCAL_ORIGIN_GROUP = 'local-group';
export const LOCAL_ORIGIN_LOCK = 'local-lock';

export const LOCAL_ORIGINS: ReadonlySet<string> = new Set([
  LOCAL_ORIGIN_DRAG,
  LOCAL_ORIGIN_CREATE,
  LOCAL_ORIGIN_EDIT,
  LOCAL_ORIGIN_DELETE,
  LOCAL_ORIGIN_RESIZE,
  LOCAL_ORIGIN_GROUP,
  LOCAL_ORIGIN_LOCK,
]);

export function getShapesMap(doc: Y.Doc): YShapeMap {
  return doc.getMap<Y.Map<unknown>>('shapes');
}

// Short opaque ID; collision odds with O(10^3) shapes per board are nil.
// Avoids crypto.randomUUID for Brave private-mode compatibility (same logic
// as useLocalUser).
const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
export function newShapeId(prefix = ''): string {
  let out = prefix;
  for (let i = 0; i < 10; i++) {
    out += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)];
  }
  return out;
}

// Add a shape to the shapes Y.Map. We populate the sub-map BEFORE inserting
// it into the parent so observers (and the P2P provider's outbound queue)
// never see a half-filled record.
//
// If `z` isn't set, stamp it as max(existing z) + 1 so new shapes land on
// top of everything currently on the board. Y.Map iteration can't be
// reordered after the fact, so render z-order rides on this field instead.
export function addShape(doc: Y.Doc, shape: Shape): void {
  const shapes = getShapesMap(doc);
  doc.transact(() => {
    const ymap = new Y.Map<unknown>();
    let z = shape.z;
    if (z === undefined) {
      let max = 0;
      shapes.forEach((m) => {
        const cur = (m.get('z') as number | undefined) ?? 0;
        if (cur > max) max = cur;
      });
      z = max + 1;
    }
    for (const [key, value] of Object.entries({ ...shape, z })) {
      if (value !== undefined) ymap.set(key, value);
    }
    shapes.set(shape.id, ymap);
  }, LOCAL_ORIGIN_CREATE);
}

// Locked shapes are skipped so Backspace/Delete can't blow them away by
// accident — the caller must unlock first or pass `force=true` (used by the
// undo manager when reverting a create).
export function deleteShapes(doc: Y.Doc, ids: Iterable<string>, force = false): void {
  const shapes = getShapesMap(doc);
  doc.transact(() => {
    for (const id of ids) {
      if (!force) {
        const ymap = shapes.get(id);
        if (ymap?.get('locked') === true) continue;
      }
      shapes.delete(id);
    }
  }, LOCAL_ORIGIN_DELETE);
}

// Patch arbitrary fields on a single shape. Used by the text editor (live
// per-keystroke writes) and the property panel. captureTimeout on the undo
// manager coalesces rapid edits into one step.
export function updateShape(
  doc: Y.Doc,
  id: string,
  patch: Record<string, unknown>,
): void {
  const shapes = getShapesMap(doc);
  const ymap = shapes.get(id);
  if (!ymap) return;
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) ymap.delete(key);
      else ymap.set(key, value);
    }
  }, LOCAL_ORIGIN_EDIT);
}

// Write the new bbox produced by a handle-drag. Same shape as updateShape
// but tagged with LOCAL_ORIGIN_RESIZE so a continuous resize gesture collapses
// into one undo step (captureTimeout=500ms) and never co-mingles with typing.
export function resizeShape(
  doc: Y.Doc,
  id: string,
  box: { x: number; y: number; w: number; h: number },
): void {
  const shapes = getShapesMap(doc);
  const ymap = shapes.get(id);
  if (!ymap) return;
  doc.transact(() => {
    ymap.set('x', box.x);
    ymap.set('y', box.y);
    ymap.set('w', box.w);
    ymap.set('h', box.h);
  }, LOCAL_ORIGIN_RESIZE);
}

// Apply a position delta to several shapes in one transaction so observers
// see one tick of changes instead of N. Continuous updates during drag are
// fine — Yjs encodes deltas tightly and our P2P provider sends each update
// as one binary frame on each open data channel.
export function moveShapesBy(
  doc: Y.Doc,
  originals: Map<string, { x: number; y: number }>,
  dx: number,
  dy: number,
): void {
  const shapes = getShapesMap(doc);
  doc.transact(() => {
    for (const [id, orig] of originals) {
      const ymap = shapes.get(id);
      if (!ymap) continue;
      ymap.set('x', orig.x + dx);
      ymap.set('y', orig.y + dy);
    }
  }, LOCAL_ORIGIN_DRAG);
}

// Read a shape's current position from Yjs. Used to capture original
// positions when a drag starts.
export function snapshotPositions(
  doc: Y.Doc,
  ids: Iterable<string>,
): Map<string, { x: number; y: number }> {
  const shapes = getShapesMap(doc);
  const out = new Map<string, { x: number; y: number }>();
  for (const id of ids) {
    const ymap = shapes.get(id);
    if (!ymap) continue;
    out.set(id, {
      x: (ymap.get('x') as number) ?? 0,
      y: (ymap.get('y') as number) ?? 0,
    });
  }
  return out;
}

// Bounding box helper for lasso intersection. Stickies/rects/ellipses/frames
// have w+h; arrows and freehand need their own AABB but Day 2 lasso only
// targets the seed shapes which all have w+h.
export function shapeBBox(shape: Shape): { x: number; y: number; w: number; h: number } | null {
  if ('w' in shape && 'h' in shape) {
    return { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
  }
  if (shape.type === 'text') {
    return { x: shape.x, y: shape.y, w: 200, h: shape.fontSize + 8 };
  }
  return null;
}

export function rectsIntersect(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

// True when `inner` is fully enclosed by `outer` (edges touching counts as
// contained). Used to decide which shapes ride along when a frame is dragged.
export function rectContains(
  outer: { x: number; y: number; w: number; h: number },
  inner: { x: number; y: number; w: number; h: number },
): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}

// Walk the selection and pull in every other shape that shares a groupId
// with anything currently in `ids`. Idempotent — passing an already-expanded
// set returns an equivalent set. Used by Canvas click/lasso handlers so a
// click on any group member selects the whole group.
export function expandSelectionToGroups(
  ids: Iterable<string>,
  shapes: readonly Shape[],
): Set<string> {
  const result = new Set(ids);
  const byId = new Map<string, Shape>();
  for (const s of shapes) byId.set(s.id, s);
  const groupIds = new Set<string>();
  for (const id of result) {
    const s = byId.get(id);
    if (s?.groupId) groupIds.add(s.groupId);
  }
  if (groupIds.size === 0) return result;
  for (const s of shapes) {
    if (s.groupId && groupIds.has(s.groupId)) result.add(s.id);
  }
  return result;
}

// When the user drags a frame, every shape visually inside that frame should
// move with it. We use geometric containment (bbox fully inside the frame's
// bbox) — there is no parent/child link in the shape model today.
//
// If a contained shape is itself a frame, its own children get picked up on
// the same pass because they're also fully inside the outer frame.
//
// Arrows and freehand return null from shapeBBox today and are skipped.
export function expandFramesToChildren(
  ids: Iterable<string>,
  shapes: readonly Shape[],
): Set<string> {
  const result = new Set(ids);
  const byId = new Map<string, Shape>();
  for (const s of shapes) byId.set(s.id, s);

  const frameBoxes: { id: string; box: { x: number; y: number; w: number; h: number } }[] = [];
  for (const id of result) {
    const shape = byId.get(id);
    if (shape?.type === 'frame') {
      frameBoxes.push({ id, box: { x: shape.x, y: shape.y, w: shape.w, h: shape.h } });
    }
  }
  if (frameBoxes.length === 0) return result;

  for (const shape of shapes) {
    if (result.has(shape.id)) continue;
    const bbox = shapeBBox(shape);
    if (!bbox) continue;
    for (const { box } of frameBoxes) {
      if (rectContains(box, bbox)) {
        result.add(shape.id);
        break;
      }
    }
  }
  return result;
}
